import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createConference } from '../../utils/api';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Button from '../../components/Button';
import DomainMultiSelect from '../../components/DomainMultiSelect';

const CreateConference = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    venue: '',
    startDate: '',
    endDate: '',
    submissionDeadline: '',
    reviewDeadline: '',
    tracks: [],
  });
  const [coOrganizerEmails, setCoOrganizerEmails] = useState([]);
  const [coOrganizerInput, setCoOrganizerInput] = useState('');
  const [currentTrack, setCurrentTrack] = useState({ name: '', description: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setFieldErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  const handleTrackChange = (e) => {
    setCurrentTrack({
      ...currentTrack,
      [e.target.name]: e.target.value,
    });
  };

  const addTrack = () => {
    if (!currentTrack.name.trim()) {
      setError('Please enter a track name');
      return;
    }
    setFormData({
      ...formData,
      tracks: [...formData.tracks, { ...currentTrack }],
    });
    setCurrentTrack({ name: '', description: '' });
    setError('');
    setFieldErrors(prev => ({ ...prev, tracks: '' }));
  };

  const removeTrack = (index) => {
    setFormData({
      ...formData,
      tracks: formData.tracks.filter((_, i) => i !== index),
    });
  };

  const addCoOrganizer = () => {
    const email = coOrganizerInput.trim().toLowerCase();
    if (!email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address for co-organizer');
      return;
    }
    if (coOrganizerEmails.includes(email)) {
      setError('This email is already added as a co-organizer');
      return;
    }
    setCoOrganizerEmails([...coOrganizerEmails, email]);
    setCoOrganizerInput('');
    setError('');
  };

  const removeCoOrganizer = (email) => {
    setCoOrganizerEmails(coOrganizerEmails.filter(e => e !== email));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    // Validation
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Please enter conference name';
    }
    if (!formData.description.trim()) {
      errors.description = 'Please enter description';
    }
    if (!formData.venue.trim()) {
      errors.venue = 'Please enter venue';
    }
    if (!formData.startDate) {
      errors.startDate = 'Please select start date';
    }
    if (!formData.endDate) {
      errors.endDate = 'Please select end date';
    }
    if (!formData.submissionDeadline) {
      errors.submissionDeadline = 'Please select submission deadline';
    }
    if (!formData.reviewDeadline) {
      errors.reviewDeadline = 'Please select review deadline';
    }
    if (formData.tracks.length === 0) {
      errors.tracks = 'Please add at least one track';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please resolve all validation errors before submitting');
      setTimeout(() => {
        const firstErrorEl = document.querySelector('.border-red-500');
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorEl.focus?.();
        }
      }, 100);
      return;
    }

    setLoading(true);
    try {
      await createConference({ ...formData, coOrganizerEmails });
      navigate('/organizer/dashboard');
    } catch (err) {
      console.error('Error creating conference:', err);
      setError(err.response?.data?.message || 'Failed to create conference');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-800 mb-4"
          >
            ← Back
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create Conference</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Fill in the details for your new conference</p>
        </div>


        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Conference Name */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Conference Name <span className="text-red-600">*</span>
              </label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="International Conference on AI 2025"
                required
                error={fieldErrors.name}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Description <span className="text-red-600">*</span>
              </label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your conference..."
                rows="4"
                required
                error={fieldErrors.description}
              />
            </div>

            {/* Venue */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Venue <span className="text-red-600">*</span>
              </label>
              <Input
                type="text"
                name="venue"
                value={formData.venue}
                onChange={handleChange}
                placeholder="City, Country or Virtual"
                required
                error={fieldErrors.venue}
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Start Date <span className="text-red-600">*</span>
                </label>
                <Input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  error={fieldErrors.startDate}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  End Date <span className="text-red-600">*</span>
                </label>
                <Input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  min={formData.startDate || new Date().toISOString().split('T')[0]}
                  required
                  error={fieldErrors.endDate}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Submission Deadline <span className="text-red-600">*</span>
                </label>
                <Input
                  type="date"
                  name="submissionDeadline"
                  value={formData.submissionDeadline}
                  onChange={handleChange}
                  max={formData.startDate}
                  required
                  error={fieldErrors.submissionDeadline}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Review Deadline <span className="text-red-600">*</span>
                </label>
                <Input
                  type="date"
                  name="reviewDeadline"
                  value={formData.reviewDeadline}
                  onChange={handleChange}
                  min={formData.submissionDeadline}
                  max={formData.startDate}
                  required
                  error={fieldErrors.reviewDeadline}
                />
              </div>
            </div>

            {/* Tracks Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Tracks <span className="text-red-600">*</span></h3>

              {/* Add Track Form */}
              <Card className={`p-4 mb-4 bg-gray-50 border transition-all duration-200 ${fieldErrors.tracks ? 'border-red-500' : 'border-gray-200'}`}>
                <div className="space-y-3">
                  <DomainMultiSelect
                    value={currentTrack.name ? [currentTrack.name] : []}
                    onChange={(domains) => setCurrentTrack({ ...currentTrack, name: domains[0] || '' })}
                    placeholder="Select a track domain..."
                    singleSelect={true}
                  />
                  <Textarea
                    name="description"
                    value={currentTrack.description}
                    onChange={handleTrackChange}
                    placeholder="Track description (optional)"
                    rows="2"
                  />
                  <Button
                    type="button"
                    onClick={addTrack}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    + Add Track
                  </Button>
                </div>
              </Card>
              {fieldErrors.tracks && <p className="mb-4 text-sm text-red-500">{fieldErrors.tracks}</p>}

              {/* Tracks List */}
              {formData.tracks.length > 0 && (
                <div className="space-y-2">
                  {formData.tracks.map((track, index) => (
                    <div key={index} className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{track.name}</p>
                        {track.description && (
                          <p className="text-sm text-gray-600">{track.description}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTrack(index)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Co-Organizers Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Co-Organizers</h3>
              <p className="text-sm text-gray-500 mb-4">
                Add organizers who will help manage this conference. They must have an existing organizer account.
              </p>

              <div className="flex gap-2 mb-3">
                <input
                  type="email"
                  value={coOrganizerInput}
                  onChange={(e) => { setCoOrganizerInput(e.target.value); setError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCoOrganizer())}
                  placeholder="co-organizer@example.com"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={addCoOrganizer}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Add
                </button>
              </div>

              {coOrganizerEmails.length > 0 && (
                <div className="space-y-2">
                  {coOrganizerEmails.map((email) => (
                    <div key={email} className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-indigo-600 text-sm">👤</span>
                        <span className="text-sm text-gray-800">{email}</span>
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">Co-Organizer</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCoOrganizer(email)}
                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-6 border-t">
              <Button
                type="button"
                onClick={() => navigate('/organizer/dashboard')}
                className="flex-1 bg-gray-600 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Creating...' : 'Create Conference'}
              </Button>
            </div>

            {/* Form-level error — shown below the submit button */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <span className="text-red-500 mt-0.5 shrink-0">⚠️</span>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </form>
        </Card>
      </div>
    </>
  );
};

export default CreateConference;
